import { QueryKey } from '@/enums';
import { TenantEntity, TwoFactorAuthentication } from '@/interfaces';
import { newQueryClient } from '@/queries/reactQueryConfig';
import { MagicLoginPayload, MagicLoginResponse, SignInPayload } from '@/types';
import { clearAuthStorage, getToken, getTokens } from '@/utils';
import axios, { AxiosResponse } from 'axios';
import { get } from 'lodash';
import APIService from './base.service';

const baseUrl = process.env.REACT_APP_BACKEND_API_URL;

export class AuthService {
  static changeTwoFactorAuthentication(
    payload: TwoFactorAuthentication,
  ): Promise<AxiosResponse> {
    const url = 'auth/SMSMfa';
    return APIService.post(url, payload);
  }

  static getRoles() {
    return APIService.get('/roles');
  }

  static getTwoFactorAuthenticationQRCode(payload: {
    accessToken: string;
    email: string;
  }): Promise<AxiosResponse> {
    return APIService.post('auth/get-qr-code', payload);
  }

  static switchMFA(payload: {
    accessToken: string;
    userCode: string;
    isEnable: boolean;
  }): Promise<AxiosResponse> {
    return APIService.post('auth/switch-mfa', payload);
  }

  // static enableMFA(payload: {
  //   accessToken: string;
  //   email: string;
  //   isEnableCustomAuthFlow?: boolean;
  // }): Promise<AxiosResponse> {
  //   const newPayload = {
  //     ...payload,
  //     isEnableCustomAuthFlow: !!payload.isEnableCustomAuthFlow,
  //   };
  //   return APIService.post('auth/enable-mfa', newPayload);
  // }

  // static disableMFA(payload: {
  //   email: string;
  //   userCode: string;
  //   userPoolId: string;
  //   accessToken: string;
  // }): Promise<AxiosResponse> {
  //   return APIService.post('auth/disable-mfa', payload);
  // }

  static recoveryMFA(payload: {
    email: string;
    recoveryValue: string;
    userPoolId: string;
  }): Promise<AxiosResponse> {
    return APIService.post('auth/mfa-recovery', payload);
  }

  static getCaptcha(): Promise<AxiosResponse> {
    return APIService.get('auth/get-captcha');
  }

  static verifyOtp(payload: {
    id?: string;
    verifyValue?: string;
    codeOtp: string;
    email: string;
    session: string;
    clientId: string;
  }): Promise<AxiosResponse> {
    return APIService.post('auth/verify-otp', payload);
  }

  static enableCustomAuth(id: string): Promise<AxiosResponse> {
    return APIService.patch(`auth/${id}/enable-custom-auth`);
  }

  static disableCustomAuth(payload: {
    id: string;
    code: string;
  }): Promise<AxiosResponse> {
    return APIService.patch(`auth/${payload.id}/disable-custom-auth`, {
      code: payload.code,
    });
  }

  static get2faSetting(email: string): Promise<AxiosResponse> {
    return APIService.get(`auth/${email}/2fa-setting`);
  }

  static sendEmailOtp(id: string): Promise<AxiosResponse> {
    return APIService.post(`auth/${id}/send-otp`);
  }

  static signIn(payload: SignInPayload): Promise<any> {
    const tenantData = newQueryClient.getQueryData([QueryKey.SUB_DOMAIN]);

    const tenant = get(tenantData, 'data', {}) as TenantEntity;

    const { id: tenantId } = tenant;

    return axios.post(baseUrl + '/auth/sign-in', payload, {
      params: { tenantId },
    });
  }

  static magicLogin(
    payload: MagicLoginPayload,
  ): Promise<AxiosResponse<MagicLoginResponse>> {
    return axios.post(baseUrl + '/auth/verify-magic-link', payload);
  }

  static signOutWithoutResolve = () => {
    const accessToken = getToken('accessToken');

    return axios.post(baseUrl + '/auth/sign-out', { accessToken });
  };

  static async signOut(): Promise<AxiosResponse<any, any>> {
    const accessToken = getToken('accessToken');

    return axios
      .post(baseUrl + '/auth/sign-out', { accessToken })
      .finally(() => clearAuthStorage());
  }

  static async getNewTokens(): Promise<any> {
    const oldToken = getTokens();
    const { refreshToken } = oldToken;

    const tenantData = newQueryClient.getQueryData([QueryKey.SUB_DOMAIN]);

    const tenant = get(tenantData, 'data', {}) as TenantEntity;

    const { id: tenantId } = tenant;

    return axios.post(
      baseUrl + '/auth/refresh-token',
      { refreshToken },
      { params: { tenantId } },
    );
  }

  static async changePassword(payload: {
    previousPassword: string;
    proposedPassword: string;
    accessToken?: string;
  }): Promise<void> {
    const { previousPassword, proposedPassword } = payload;
    const accessToken = getToken('accessToken') || payload?.accessToken || '';

    const tenantData = newQueryClient.getQueryData([QueryKey.SUB_DOMAIN]);

    const tenant = get(tenantData, 'data', {}) as TenantEntity;

    const { id: tenantId } = tenant;

    return axios.post(
      baseUrl + '/auth/change-password',
      {
        accessToken,
        previousPassword,
        proposedPassword,
      },
      { params: { tenantId } },
    );
  }

  static async resetPasswordChallenge(payload: {
    password: string;
    token: string;
    subdomain: string;
  }) {
    return axios.post(baseUrl + '/auth/challenge-reset-password', payload);
  }

  static async setAcceptTermsOfService(payload: {
    userId: string;
    tenantId: string;
  }): Promise<void> {
    const { userId, tenantId } = payload;
    return APIService.post(
      baseUrl + '/auth/accept-terms-of-service',
      { userId },
      {
        params: { tenantId },
      },
    );
  }

  static getSubdomain(): Promise<string> {
    return APIService.get('/auth/subdomain');
  }

  static initForgotPassword(payload: {
    email: string;
    tenantId: string;
  }): Promise<any> {
    return APIService.post('/auth/init-forgot-password', payload);
  }

  static confirmForgotPassword(payload: {
    email: string;
    verificationCode: string;
    password: string;
    tenantId: string;
  }): Promise<any> {
    return APIService.post('/auth/confirm-forgot-password', payload);
  }
}
